import React, { useState, useEffect } from 'react';
import { Download, Filter, CreditCard, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button, Input } from '../ui';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import { PAYMENT_CHANNELS } from '../../utils/bankAccounts';

const PaymentToVendorsReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    vendorId: '',
    paymentMethod: 'all',
    itineraryId: '',
    guestName: ''
  });
  const [vendors, setVendors] = useState<string[]>([]);
  const [summary, setSummary] = useState({
    totalAmountIDR: 0,
    totalPayments: 0
  });

  useEffect(() => {
    loadVendors();
    loadPayments();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await api.get('/accounts-v3/vendors');
      if (response.data.success) {
        setVendors(['All Vendors', ...response.data.data]);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.vendorId) params.append('vendor_name', filters.vendorId);
      if (filters.paymentMethod !== 'all') params.append('payment_method', filters.paymentMethod);
      if (filters.itineraryId) params.append('itinerary_id', filters.itineraryId);
      if (filters.guestName) params.append('guest_name', filters.guestName);

      const response = await api.get(`/reports/payments-to-vendors?${params.toString()}`);
      if (response.data.success) {
        setPayments(response.data.data.payments);
        setSummary(response.data.data.summary);
      }
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPayments = () => {
    if (!sortConfig) return payments;

    const sorted = [...payments].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'payment_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortConfig.key === 'payment_amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  const exportToExcel = () => {
    try {
      const exportData = getSortedPayments().map(payment => ({
        'Date': formatDate(payment.payment_date),
        'Itinerary': payment.itinerary_id,
        'Guest': payment.guest_name,
        'Vendor': payment.vendor_name,
        'Amount': payment.payment_amount,
        'Payment Method': payment.bank_account || 'N/A',
        'Reference': payment.payment_reference || 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payments to Vendors');
      
      const summaryData = [
        { Metric: 'Total Amount (IDR)', Value: summary.totalAmountIDR },
        { Metric: 'Total Payments', Value: summary.totalPayments },
        { Metric: 'Date Range', Value: `${filters.startDate} to ${filters.endDate}` }
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      
      XLSX.writeFile(wb, `payments_to_vendors_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">💸 Payment to Vendors Report</h1>
        <p className="text-gray-600">Track payments made to vendors through Accounts V3</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCard className="w-8 h-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Amount (IDR)</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalAmountIDR)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">#</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Payments</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalPayments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
            <Input
              type="text"
              value={filters.itineraryId}
              onChange={(e) => handleFilterChange('itineraryId', e.target.value)}
              placeholder="e.g. 5749"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Guest Name</label>
            <Input
              type="text"
              value={filters.guestName}
              onChange={(e) => handleFilterChange('guestName', e.target.value)}
              placeholder="Guest name"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <SearchableSelect
              options={vendors.map(vendor => ({ value: vendor === 'All Vendors' ? '' : vendor, label: vendor }))}
              value={filters.vendorId}
              onChange={(value) => handleFilterChange('vendorId', value as string)}
              placeholder="All Vendors"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <SearchableSelect
              options={[
                { value: 'all', label: 'All Methods' },
                ...PAYMENT_CHANNELS.map(method => ({ value: method, label: method }))
              ]}
              value={filters.paymentMethod}
              onChange={(value) => handleFilterChange('paymentMethod', value as string)}
              placeholder="All Methods"
              className="w-full"
            />
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button onClick={loadPayments} disabled={loading}>
            {loading ? 'Loading...' : 'Search'}
          </Button>
          <Button onClick={exportToExcel} variant="secondary" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Payment Results ({payments.length})</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('payment_date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {getSortIcon('payment_date')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('itinerary_id')}
                >
                  <div className="flex items-center gap-1">
                    Itinerary
                    {getSortIcon('itinerary_id')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('guest_name')}
                >
                  <div className="flex items-center gap-1">
                    Guest
                    {getSortIcon('guest_name')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('vendor_name')}
                >
                  <div className="flex items-center gap-1">
                    Vendor
                    {getSortIcon('vendor_name')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('payment_amount')}
                >
                  <div className="flex items-center gap-1">
                    Amount
                    {getSortIcon('payment_amount')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('bank_account')}
                >
                  <div className="flex items-center gap-1">
                    Payment Method
                    {getSortIcon('bank_account')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedPayments().map((payment, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(payment.payment_date)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    WH_{payment.itinerary_id}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-32 truncate" title={payment.guest_name}>
                    {payment.guest_name}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.vendor_name}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(payment.payment_amount)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      {payment.bank_account || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-32 truncate" title={payment.payment_reference}>
                    {payment.payment_reference || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {payments.length === 0 && !loading && (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No payments found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentToVendorsReportPage;
